import { Formik, FormikConfig, FormikErrors, FormikProps } from "formik";
import { type JSX } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  accountBodyClassName,
  accountCardClassName,
  accountHeaderClassName,
} from "./accountFormLayout";
import { ProfileFormInput, SubmitButton } from "./ProfileFormInputs";
import {
  accountFormKeys,
  AccountFormValues,
  FormValues,
  sliceProfileValues,
} from "./support";
import { DSA_FAQ_URL, EMAIL_UPDATES_ENABLED } from "utils/constants";
import { validateEmail } from "utils/validators";
import {
  ActionButton,
  Banner,
  Card,
  CardTitle,
  Input,
  typography,
} from "@dsaui/components";
import { cn } from "@dsaui/utils";
import routes from "routes";

interface AccountFormProps {
  profile: FormValues;
  handleProfileUpdate: (values: AccountFormValues) => Promise<void>;
  ready: boolean;
}

const validateForm = (values: AccountFormValues): FormikErrors<AccountFormValues> => {
  const errors: FormikErrors<AccountFormValues> = {};
  if (EMAIL_UPDATES_ENABLED) {
    const emailError = validateEmail(values);
    if (emailError) errors.email = emailError;
  }
  return errors;
};

const AccountForm = ({
  profile,
  handleProfileUpdate,
  ready,
}: AccountFormProps): JSX.Element => {
  const { t } = useTranslation();
  const initialValues = sliceProfileValues(accountFormKeys, profile);

  const submitForm: FormikConfig<AccountFormValues>["onSubmit"] = async (
    values,
    { setSubmitting },
  ): Promise<void> => {
    try {
      await handleProfileUpdate(values);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik<AccountFormValues>
      enableReinitialize
      initialValues={initialValues}
      validate={validateForm}
      onSubmit={submitForm}
      validateOnMount
    >
      {(fp: FormikProps<AccountFormValues>): JSX.Element => (
        <form
          aria-label="account form"
          className="flex flex-col gap-8"
          onSubmit={fp.handleSubmit}
        >
          <Card className={accountCardClassName}>
            <div className={accountHeaderClassName}>
              <CardTitle>{t("forms.account.updateName.title")}</CardTitle>
            </div>
            <div className={accountBodyClassName}>
              <div className="flex flex-col gap-6 lg:flex-row lg:gap-6">
                <div className="min-w-0 flex-1">
                  <ProfileFormInput
                    fp={fp}
                    label={t("forms.account.firstName")}
                    name="chosenFirstName"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <ProfileFormInput
                    fp={fp}
                    label={t("forms.account.lastName")}
                    name="chosenLastName"
                  />
                </div>
              </div>
              <Banner>
                <Trans
                  components={{
                    1: (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a
                        className={typography.profileLink.className}
                        href={routes.internal.profile}
                      />
                    ),
                  }}
                  i18nKey="forms.account.updateName.profileBanner"
                />
              </Banner>
              <SubmitButton block fp={fp} ready={ready}>
                {t("save")}
              </SubmitButton>
            </div>
          </Card>

          <Card className={accountCardClassName}>
            <div className={accountHeaderClassName}>
              <CardTitle>{t("forms.account.updateEmail.title")}</CardTitle>
            </div>
            <div className={accountBodyClassName}>
              <Banner>
                <Trans
                  components={{
                    1: (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects link text
                      <a
                        className={cn(typography.profileLink.className, "break-all")}
                        href={DSA_FAQ_URL}
                        rel="noreferrer"
                        target="_blank"
                      />
                    ),
                  }}
                  i18nKey="forms.account.updateEmail.faqBanner"
                />
              </Banner>
              <Input
                disabled
                id="email"
                label={t("forms.labels.email")}
                name="email"
                readOnly
                value={initialValues.email ?? ""}
              />
              <ActionButton disabled fullWidth>
                {t("save")}
              </ActionButton>
            </div>
          </Card>
        </form>
      )}
    </Formik>
  );
};

export default AccountForm;
